import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Heart, ChevronDown, Globe, Landmark, Menu, User, X } from "lucide-react";
import { useLang, pick } from "@/i18n";
import { getFavorites, subscribeFavorites } from "@/utils/favorites";

const Logo = () => (
    <span className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-lg border border-amber-500/60 bg-amber-400/10 text-amber-400">
            <Landmark size={19} />
        </span>
        <span className="leading-none">
            <span className="block font-display text-lg tracking-[0.18em] text-amber-300">TOURVOYAGERS</span>
            <span className="mt-1 block text-[7.5px] tracking-[0.34em] text-amber-500/80 uppercase">Rome & Italy Experiences</span>
        </span>
    </span>
);

export const Navbar = () => {
    const { lang, setLang, t, locales } = useLang();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [favOpen, setFavOpen] = useState(false);
    const [userOpen, setUserOpen] = useState(false);
    const [langOpen, setLangOpen] = useState(false);
    const [favs, setFavs] = useState(getFavorites());
    const location = useLocation();

    useEffect(() => subscribeFavorites(setFavs), []);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setOpen(false); setFavOpen(false); setUserOpen(false); setLangOpen(false); }, [location.pathname]);

    const links = [
        { to: "/esperienze", label: t.nav.experiences, testid: "nav-experiences" },
        { to: "/itinerario", label: t.nav.createItinerary, testid: "nav-builder", match: "/itinerario" },
        { to: "/alloggi", label: t.nav.stays, testid: "nav-stays" },
        { to: "/transfer", label: t.nav.transfers, testid: "nav-transfers" },
        { to: "/#destinazioni", label: t.nav.destinations, testid: "nav-destinations", anchor: "destinazioni" },
        { to: "/chi-siamo", label: t.nav.about, testid: "nav-about" },
    ];

    const go = (l) => {
        if (!l.anchor) return;
        if (location.pathname === "/") {
            document.getElementById(l.anchor)?.scrollIntoView({ behavior: "smooth" });
        } else {
            window.location.href = `/#${l.anchor}`;
        }
    };

    return (
        <header
            data-testid="main-navbar"
            className="fixed top-2 inset-x-2 sm:top-3.5 sm:inset-x-3.5 z-50"
        >
            <div className={`mx-auto flex h-[68px] max-w-[92rem] items-center justify-between rounded-2xl border px-4 transition-colors duration-500 sm:px-6 ${
                scrolled ? "border-amber-500/25 bg-black/85 backdrop-blur-xl shadow-[0_12px_40px_rgba(0,0,0,0.55)]" : "border-amber-500/15 bg-black/35 backdrop-blur-md"
            }`}>
                <Link to="/" data-testid="nav-logo" aria-label="TOURVOYAGERS home">
                    <Logo />
                </Link>

                <nav className="hidden xl:flex items-center gap-4 2xl:gap-8">
                    {links.map((l) => (
                        l.anchor ? (
                            <button
                                key={l.to}
                                onClick={() => go(l)}
                                data-testid={l.testid}
                                className="text-[11.5px] 2xl:text-[13px] tracking-[0.08em] 2xl:tracking-[0.14em] uppercase whitespace-nowrap transition-colors duration-300 hover:text-amber-300 text-stone-300"
                            >
                                {l.label}
                            </button>
                        ) : (
                            <Link
                                key={l.to}
                                to={l.to}
                                data-testid={l.testid}
                                className={`text-[11.5px] 2xl:text-[13px] tracking-[0.08em] 2xl:tracking-[0.14em] uppercase whitespace-nowrap transition-colors duration-300 hover:text-amber-300 ${
                                    (l.exact ? location.pathname === l.to : location.pathname.startsWith(l.match || l.to)) ? "text-amber-300" : "text-stone-300"
                                }`}
                            >
                                {l.label}
                            </Link>
                        )
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    {/* Selettore lingua internazionale: 16 lingue, nomi in forma nativa */}
                    <div className="relative">
                        <button
                            data-testid="language-selector"
                            onClick={() => { setLangOpen((o) => !o); setFavOpen(false); setUserOpen(false); }}
                            aria-label={t.nav.languageLabel || "Lingua"}
                            className="flex items-center gap-1.5 rounded-full border border-amber-500/30 px-3 py-1.5 text-[11px] font-semibold tracking-widest uppercase text-stone-300 transition-colors hover:text-amber-300 hover:border-amber-400/60"
                        >
                            <Globe size={13} className="text-amber-400" />
                            {lang === "zh-hans" ? "简体" : lang === "zh-hant" ? "繁體" : lang.split("-")[0]}
                            <ChevronDown size={11} className={`transition-transform ${langOpen ? "rotate-180" : ""}`} />
                        </button>
                        <AnimatePresence>
                            {langOpen && (
                                <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                                    className="absolute right-0 top-full z-50 mt-2 max-h-80 w-52 overflow-y-auto rounded-xl border border-amber-500/25 bg-[#101011] shadow-[0_18px_50px_rgba(0,0,0,0.6)]" data-testid="language-menu">
                                    {locales.map((l) => (
                                        <button
                                            key={l.code}
                                            data-testid={`language-option-${l.code}`}
                                            onClick={() => { setLang(l.code); setLangOpen(false); }}
                                            className={`flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${lang === l.code ? "bg-amber-400/10 text-amber-300" : "text-stone-300 hover:bg-amber-400/5 hover:text-amber-200"}`}
                                        >
                                            <span className="w-8 shrink-0 text-[10px] font-bold uppercase tracking-wider text-amber-500/80">{l.code.split("-")[0]}</span>
                                            {l.label}
                                        </button>
                                    ))}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                    <div className="relative hidden xl:block">
                        <button data-testid="nav-favorites" onClick={() => { setFavOpen((o) => !o); setUserOpen(false); }} aria-label={t.nav.favorites}
                            className="relative flex h-9 w-9 items-center justify-center rounded-full border border-amber-500/30 text-stone-300 transition-colors hover:text-amber-300 hover:border-amber-400/60">
                            <Heart size={15} className={favs.length ? "fill-amber-400 text-amber-400" : ""} />
                            {favs.length > 0 && (
                                <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-400 px-1 text-[9px] font-bold text-black" data-testid="nav-favorites-count">{favs.length}</span>
                            )}
                        </button>
                        <AnimatePresence>
                            {favOpen && (
                                <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                                    className="absolute right-0 top-full z-50 mt-2 w-72 overflow-hidden rounded-xl border border-amber-500/25 bg-[#101011] shadow-[0_18px_50px_rgba(0,0,0,0.6)]" data-testid="nav-favorites-panel">
                                    <p className="border-b border-amber-500/15 px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.18em] text-amber-400">{t.nav.favorites}</p>
                                    {favs.length === 0 && <p className="px-4 py-4 text-xs text-stone-500">{t.nav.favoritesEmpty}</p>}
                                    {favs.map((f) => (
                                        <Link key={f.id} to={f.link} data-testid={`nav-fav-${f.id}`} className="block px-4 py-2.5 text-xs text-stone-200 hover:bg-amber-400/5 hover:text-amber-200">{pick(f.name, lang)}</Link>
                                    ))}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                    <div className="relative hidden xl:block">
                        <button data-testid="nav-account" onClick={() => { setUserOpen((o) => !o); setFavOpen(false); }} aria-label={t.nav.account}
                            className="flex h-9 w-9 items-center justify-center rounded-full border border-amber-500/30 text-stone-300 transition-colors hover:text-amber-300 hover:border-amber-400/60">
                            <User size={15} />
                        </button>
                        <AnimatePresence>
                            {userOpen && (
                                <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                                    className="absolute right-0 top-full z-50 mt-2 w-60 overflow-hidden rounded-xl border border-amber-500/25 bg-[#101011] shadow-[0_18px_50px_rgba(0,0,0,0.6)]" data-testid="nav-account-panel">
                                    <Link to="/stato" data-testid="nav-account-status" className="block px-4 py-3 text-xs text-stone-200 hover:bg-amber-400/5 hover:text-amber-200">{t.nav.statusPage}</Link>
                                    <Link to="/admin" data-testid="nav-account-admin" className="block border-t border-amber-500/10 px-4 py-3 text-xs text-stone-200 hover:bg-amber-400/5 hover:text-amber-200">{t.nav.admin}</Link>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                    <Link
                        to="/itinerario"
                        data-testid="nav-book-now"
                        className="hidden xl:inline-flex items-center rounded-full bg-amber-400 px-4 py-2 text-[10px] 2xl:px-5 2xl:text-[12px] font-bold tracking-[0.1em] 2xl:tracking-[0.12em] uppercase text-black transition-all duration-300 hover:bg-amber-300 hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] whitespace-nowrap"
                    >
                        {t.nav.bookNow}
                    </Link>
                    <button
                        data-testid="nav-mobile-toggle"
                        onClick={() => setOpen(!open)}
                        className="xl:hidden text-amber-200 p-2"
                        aria-label="Menu"
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="xl:hidden overflow-hidden bg-black/95 backdrop-blur-xl border-t border-amber-500/15"
                        data-testid="nav-mobile-menu"
                    >
                        <div className="px-6 py-6 flex flex-col gap-5">
                            {links.map((l) => (
                                l.anchor ? (
                                    <button key={l.to} onClick={() => { setOpen(false); go(l); }} data-testid={`${l.testid}-mobile`} className="text-left text-sm tracking-[0.18em] uppercase text-stone-200 hover:text-amber-300 transition-colors">
                                        {l.label}
                                    </button>
                                ) : (
                                    <Link key={l.to} to={l.to} data-testid={`${l.testid}-mobile`} className="text-sm tracking-[0.18em] uppercase text-stone-200 hover:text-amber-300 transition-colors">
                                        {l.label}
                                    </Link>
                                )
                            ))}
                            <Link to="/itinerario" data-testid="nav-book-now-mobile" className="mt-2 inline-flex w-fit items-center rounded-full bg-amber-400 px-6 py-2.5 text-xs font-bold tracking-[0.12em] uppercase text-black">
                                {t.nav.bookNow}
                            </Link>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
};
